<template>
  <div class="billing-product-card" v-if="product">
    <div class="billing-product-top">
      <h1 class="billing-product-title">{{product.name}}</h1>
      <div class="billing-product-description">{{product.description}}</div>
      <div class="billing-price-container">
        <div v-if="product.displayPrice" class="billing-product-price">
          $<span class="billing-product-price-amount">{{formatDecimal(priceAmount, 2, true)}}</span><span class="billing-product-price-interval">{{priceIntervalSuffix}}</span>
        </div>
        <div v-if="product.displayPrice" class="billing-product-interval-select">
          <a v-if="isMonthlyPriceAvailable" href="#" :class="{'active': isMonthlyPriceSelected}" @click.prevent="priceInterval = 'month'">Monthly Billing</a> <span v-if="isMonthlyPriceAvailable && isYearlyPriceAvailable">|</span>
          <a v-if="isYearlyPriceAvailable" href="#" :class="{'active': isYearlyPriceSelected}" @click.prevent="priceInterval = 'year'">Yearly Billing</a>
        </div>
        <div v-if="!product.displayPrice" class="billing-product-no-price text-muted">
          Get in contact with our sales team for a customised quote to suit your needs
        </div>
        <FormButton v-if="!product.displayPrice" :loading="submitting" class="btn-lg billing-contact-us btn-primary" @click="contactClicked"><i class="far fa-envelope" /> Contact Us</FormButton>
        <FormButton v-if="product.displayPrice" :loading="submitting" class="btn-lg billing-btn-purchase btn-success" @click="purchaseClicked"><i class="far fa-shopping-cart" /> {{purchaseButtonTitle}}</FormButton>
      </div>
    </div>
    
    <div class="billing-product-features-container">
      <div v-if="!product.isUserProduct">
        <strong>Plan Includes:</strong>
      </div>
      <div v-if="product.isUserProduct">
        <strong>User Quantity:</strong>
      </div>
      <div class="billing-product-feature" v-if="!product.displayPrice && product.isUserProduct">
        <i class="far fa-check-circle" />{{includedUserCount}} User{{displayCountPlural(includedUserCount)}}
      </div>
      <div class="billing-product-feature" v-if="product.displayPrice && product.isUserProduct">
        <FormSelect :options="userCountOptions" v-model="usersRequired" />
      </div>
      <div class="billing-product-feature" v-for="publicFeature of product.publicFeatureList" :key="publicFeature">
        <i class="far fa-check-circle" /> {{publicFeature}}
      </div>
    </div>

  </div>
</template>

<style scoped>
.billing-product-card {
  display: block;
  text-align: center;
  min-height: 400px;
  padding: 0px 20px;
}
.billing-product-top {
  height: 275px;
  border-bottom: 1px solid #f7f7f7;
}
.billing-product-title {
  margin-bottom: 20px;
  font-weight: 500;
}
.billing-product-description {
  font-size: 14px;
  vertical-align: middle;  
}
.billing-product-price {
  font-weight: bold;
  font-size: 22px;
}
.billing-product-price-amount {
  font-weight: bold;
  font-size: 64px;
}
.billing-product-price-interval {
  font-weight: normal;
  font-size: 18px;
}
.billing-product-no-price {
  font-size: 14px;
  vertical-align: middle;
  margin-top: 20px;
}
.billing-price-container {
  height: 130px;
}
.billing-product-interval-select {
  font-size: 11px;
}
.billing-product-interval-select > a {
  color: inherit;
}
.billing-product-interval-select > a.active {
  color: #007bff;
}
.billing-contact-us {
  margin-top: 40px;
}
.billing-btn-purchase {
  margin-top: 10px;
}
.billing-product-features-container {
  text-align: left;
  font-size: 14px;
  margin: 0px 20px;
  padding-top: 20px;
}
.billing-product-feature {
  margin: 10px 0;
}
.billing-product-feature > i.far {
  margin-right: 8px;
}
</style>


<script>
import Utils from '@/utils/utils'

export default {
  name: 'BillingProductCard',
  props: {
    product: Object,
    productList: Array,
    purchaseButtonTitle: {
      type: String,
      default: 'Checkout'
    },
    submitting: Boolean
  },
  data() {
    return {
      priceInterval: 'month',
      usersRequired: 0
    }
  },
  computed: {
    priceAmount() {
      let currentPrice = this.getSelectedPrice()
      let additionalUserProduct = this.additionalUserProduct
      
      if(currentPrice) {
        let amount = currentPrice.amount

        if(additionalUserProduct) {
          let userProductPrice = additionalUserProduct.pricing.find(priceInfo => priceInfo.interval == this.priceInterval)
          
          if(userProductPrice) {
            let additionalUserCount = this.usersRequired - this.includedUserCount
            if(additionalUserCount > 0)
              amount += additionalUserCount * userProductPrice.amount
          }
        }
        return amount
      }
      return ''
    },
    priceIntervalSuffix() {
      let currentPrice = this.getSelectedPrice()

      if(currentPrice && this.priceInterval == 'month')
        return '/mo'
      if(currentPrice && this.priceInterval == 'year')
        return '/yr'
      return ''
    },
    isMonthlyPriceAvailable() {
      if(!this.product)
        return false
      return this.product.pricing.find(priceInfo => priceInfo.interval == 'month') != null
    },
    isYearlyPriceAvailable() {
      if(!this.product)
        return false
      return this.product.pricing.find(priceInfo => priceInfo.interval == 'year') != null
    },
    isOnTimePriceAvailable() {
      if(!this.product)
        return false
      return this.product.pricing.find(priceInfo => priceInfo.interval == 'one_time') != null
    },
    isMonthlyPriceSelected() {
      return this.priceInterval == 'month'
    },
    isYearlyPriceSelected() {
      return this.priceInterval == 'year'
    },
    isOneTimePriceSelected() {
      return this.priceInterval == 'one_time'
    },
    includedUserCount() {
      if(!this.product)
        return 0
      return this.product.availableUserCount
    },
    additionalUserProduct() {
      if(!this.productList)
        return null
      let productIndex = 0
      return this.productList.filter(product => product.isUserProduct)[productIndex]
    },
    userCountOptions() {
      if(!this.product)
        return 0

      let includedUsers = this.product.availableUserCount || 1

      var optionsList = []
      if(!this.product.isUserProduct && !this.product.isAddonProduct) {
        optionsList.push({ name: `${includedUsers} User${Utils.formatCountPlural(includedUsers)} (included)`, value: includedUsers })
        if(this.additionalUserProduct) {
          for(let i=0; i<9; i++) {
            let userCount = includedUsers + i + 1
            optionsList.push({
              name: `${userCount} User${Utils.formatCountPlural(userCount)}`, value:userCount
            })
          }
        }
      }

      if(this.product.isUserProduct && this.product.isAddonProduct) {
        for(let i=0; i<20; i++) {
          let userCount = i + 1
          optionsList.push({
            name: `${userCount} User${Utils.formatCountPlural(userCount)}`, value:userCount
          })
        }
      }

      return optionsList
    }
  },
  mounted() {
    // Default price interval
    if(!this.isMonthlyPriceAvailable) {
      if(this.product && this.product.pricing.length > 0) {
        this.priceInterval = this.product.pricing[0].interval
      }
    }
    // Default users required
    if(this.product && this.product.isUserProduct) {
      this.usersRequired = 1
    }
  },
  methods: {
    getSelectedPrice() {
      if(!this.product)
        return null
      return this.product.pricing.find(priceInfo => priceInfo.interval == this.priceInterval)
    },
    getMonthlyPrice() {
      if(!this.product)
        return null
      return this.product.pricing.find(priceInfo => priceInfo.interval == 'month')
    },
    getYearlyPrice() {
      if(!this.product)
        return null
      return this.product.pricing.find(priceInfo => priceInfo.interval == 'year')
    },
    updateSelection(selected) {
      if(selected)
        this.$emit('click')
    },
    purchaseClicked() {
      let quantity = 1

      // This product is a user addon
      if(this.product.isUserProduct && this.product.isAddonProduct) {
        quantity = this.usersRequired
      }

      let priceLineItems = [{
        id: this.getSelectedPrice().stripeId,
        quantity: quantity,
        interval: this.getSelectedPrice().interval
      }]
      
      // Included users + user addon
      if(!this.product.isUserProduct && !this.product.isAddonProduct) {
        if(this.additionalUserProduct) {
          let userProductPrice = this.additionalUserProduct.pricing.find(priceInfo => priceInfo.interval == this.priceInterval)

          if(userProductPrice) {
            let additionalUserCount = this.usersRequired - this.includedUserCount
            if(additionalUserCount > 0) {
              priceLineItems.push({
                id: userProductPrice.stripeId,
                quantity: additionalUserCount,
                interval: userProductPrice.interval
              })
            }
          }
        }
      }

      this.$emit('purchaseClicked', {
        id: this.product.id,
        priceLineItems: priceLineItems
      })
    },
    contactClicked() {
      this.$emit('contactClicked', {
        id: this.product.id,
        name: this.product.name
      })
    }

  }
}
</script>